import { useCallback, useState } from 'react';

import type { MessageReference } from '@/components/chat/bubbles/references/messageReferences';
import { useChatAnalytics } from '@/hooks/chat/useChatAnalytics';
import {
  type ReferenceDetailsEntryPoint,
  trackReferenceDetailsViewed,
  trackReferenceViewed,
} from '@/util/analyticsEvents';
import { getReferenceUrlHost, openReferenceUrl } from '@/util/chat/referenceUtils';

/**
 * Open state for a message's reference sheet, held by the bubble so it sits
 * outside the sheet's portal.
 *
 * The analytics for opening a source fire from here too, not from inside the
 * sheet. The native sheet portals to the root overlay, beyond the chat's
 * analytics provider, so an event tracked in there loses the inquiry and
 * recipient fields every other chat event carries.
 *
 * The sheet remembers which work is open, not its place in the list. A share
 * arriving while the sheet is open can land ahead of it in reading order and
 * renumber the rest, and a position would then silently show another work.
 * If the open work leaves the list, as when a stronger share replaces a weaker
 * one, the sheet stays at the same place, clamped to the end; an emptied list
 * closes it.
 *
 * `totalAttached` is the statement footnote count, pending ones included,
 * that `Reference viewed` reported before the sheet existed.
 */
export function useReferenceSheet({
  references,
  conversationId,
  messageSid,
  totalAttached = 0,
}: {
  references: readonly MessageReference[];
  conversationId: string;
  messageSid: string;
  totalAttached?: number;
}) {
  const { track } = useChatAnalytics();

  const [open, setOpen] = useState<{ key: string; index: number } | null>(null);

  const openIndex = resolveOpenIndex(references, open);

  const show = useCallback(
    (index: number, entryPoint: ReferenceDetailsEntryPoint) => {
      const reference = references[index];

      if (!reference) {
        return;
      }

      setOpen({ key: referenceKey(reference), index });

      trackReferenceDetailsViewed(track, {
        conversationId,
        messageSid,
        referenceNumber: reference.number,
        totalOnMessage: references.length,
        entryPoint,
        hasPublication: reference.publication !== undefined,
        shareOrigin: reference.publication?.origin,
        publicationKind: reference.publication?.kind,
      });
    },
    [conversationId, messageSid, references, track]
  );

  const openNumber = useCallback(
    (number: number, entryPoint: ReferenceDetailsEntryPoint) => {
      show(
        references.findIndex((candidate) => candidate.number === number),
        entryPoint
      );
    },
    [references, show]
  );

  const openReference = useCallback(
    (reference: MessageReference) => openNumber(reference.number, 'footnote'),
    [openNumber]
  );

  const openCitation = useCallback(
    (number: number) => openNumber(number, 'inline_citation'),
    [openNumber]
  );

  const setIndex = useCallback((index: number) => show(index, 'navigation'), [show]);

  const close = useCallback(() => setOpen(null), []);

  const openSource = useCallback(
    async (reference: MessageReference) => {
      trackReferenceViewed(track, {
        conversationId,
        messageSid,
        url: reference.url,
        urlHost: getReferenceUrlHost(reference.url),
        referenceNumber: reference.number,
        totalAttachedOnMessage: totalAttached,
        totalOnMessage: references.length,
        statementIndex: reference.statementIndex,
        statementType: reference.statementType,
        shareOrigin: reference.publication?.origin,
        publicationKind: reference.publication?.kind,
      });

      await openReferenceUrl(reference.url);
    },
    [conversationId, messageSid, references, totalAttached, track]
  );

  return {
    openIndex,
    openReference,
    openCitation,
    openNumber,
    setIndex,
    close,
    openSource,
  };
}

const referenceKey = (reference: MessageReference) =>
  reference.publication?.shareId ?? `url:${reference.url}`;

function resolveOpenIndex(
  references: readonly MessageReference[],
  open: { key: string; index: number } | null
): number | null {
  if (open === null || references.length === 0) {
    return null;
  }

  const index = references.findIndex((reference) => referenceKey(reference) === open.key);

  return index === -1 ? Math.min(open.index, references.length - 1) : index;
}
